import {
  DvGridComponent,
  DvToolbarAction,
  DvToolbarActionEvent,
} from '@devinco/ui/grid';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TripTypeSetupDialogComponent } from '@features/trip-type-setup/trip-type-setup-dialog/trip-type-setup-dialog.component';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { DirectivesModule } from '@shared/directives/directives.module';
import { AgGridModule } from 'ag-grid-angular';
import {
  GridApi,
  GridReadyEvent,
  IRowNode,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { TimeInterval } from '../interfaces';
import { TripTypeSetupGridService } from './trip-type-setup-grid.service';
import { DialogService } from '@core/services/dialog.service';
import { Entity } from '@shared/constants/entity';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-trip-type-setup-grid',
  templateUrl: './trip-type-setup-grid.component.html',
  styleUrls: ['./trip-type-setup-grid.component.scss'],
  imports: [CommonModule, AgGridModule, DvGridComponent, DirectivesModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [TripTypeSetupGridService, GridOptionsService],
})
export class TripTypeSetupGridComponent implements OnInit, OnChanges {
  private gridOptionsService = inject(GridOptionsService);
  private readonly gridService = inject(TripTypeSetupGridService);
  private readonly dialog = inject(DialogService);
  private readonly translate = inject(TranslateService);

  @Input() rowData: TimeInterval[] = [];
  @Input() hasEditPermission: boolean;
  @Input() hasDeletePermission: boolean;
  @Input() entityName: Entity;
  @Input() noRowsMessageKey?: string;

  @Output() hasGridDataChanged = new EventEmitter<TimeInterval[]>();
  @Output() gridReloadClick = new EventEmitter<void>();
  actions = signal<DvToolbarAction[]>([]);
  gridOptions: GridOptions;

  private selectedNode: IRowNode | undefined;
  private gridApi: GridApi | undefined;
  private defaultNoRowsMessage?: string;

  ngOnInit(): void {
    this.gridOptions = this.gridOptionsService.vtCore(this.gridService.colDefs);
    this.defaultNoRowsMessage =
      this.gridOptions.noRowsOverlayComponentParams?.message;
    this.applyNoRowsMessage();
    this.actions.set(
      this.gridService.getToolbarActions(
        this.onEditClick,
        this.onAddClick,
        this.onReloadClick,
        this.onDeleteClick
      )
    );
    this.performAccessControl();
    this.gridOptions.context.entity = this.entityName;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'hasEditPermission' in changes ||
      ('hasDeletePermission' in changes && this.actions().length)
    ) {
      this.performAccessControl();
    }
    if (
      'noRowsMessageKey' in changes &&
      !changes['noRowsMessageKey'].firstChange
    ) {
      this.applyNoRowsMessage();
      if (this.gridApi) {
        this.gridApi.setGridOption(
          'noRowsOverlayComponentParams',
          this.gridOptions.noRowsOverlayComponentParams
        );
        if (!this.gridApi.getDisplayedRowCount()) {
          this.gridApi.showNoRowsOverlay();
        }
      }
    }
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  onRowDataChanged(): void {
    const displayedRowCount = this.gridApi?.getDisplayedRowCount() ?? 0;
    if (!displayedRowCount) {
      this.gridApi?.deselectAll();
      this.selectedNode = undefined;
      this.gridApi?.showNoRowsOverlay();
    } else {
      this.gridApi?.hideOverlay();
      this.gridApi?.getDisplayedRowAtIndex(0)?.setSelected(true, true);
    }
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    const selectedNode = api.getSelectedNodes()[0];
    this.selectedNode = selectedNode;

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'edit') {
          return {
            ...action,
            disabled: !this.hasEditPermission || !selectedNode,
          };
        }
        if (action.icon === 'delete') {
          return {
            ...action,
            disabled: !this.hasDeletePermission || !selectedNode,
          };
        }
        return action;
      })
    );
  }

  openAddDialog(): void {
    if (this.gridApi) {
      this.onAddClick({
        api: this.gridApi,
        context: this.gridApi.getGridOption('context'),
      });
    }
  }

  private onAddClick = (event?: DvToolbarActionEvent): void => {
    this.openModal('TripTypeSetupInformationTab.Add', undefined, event);
  };

  private onEditClick = (event?: DvToolbarActionEvent): void => {
    this.openModal(
      'TripTypeSetupInformationTab.Edit',
      this.selectedNode?.data,
      event
    );
  };

  private onReloadClick = (): void => {
    this.gridReloadClick.emit();
  };

  private onDeleteClick = (event?: DvToolbarActionEvent): void => {
    const selectedNode = event?.api.getSelectedNodes()[0];
    if (selectedNode) {
      event.api.applyTransaction({ remove: [selectedNode.data] });
      this.emitGridDataChanged(event);
    }
  };

  private openModal(
    title: string,
    data?: TimeInterval,
    event?: DvToolbarActionEvent
  ): void {
    this.dialog
      .open<TimeInterval, TripTypeSetupDialogComponent>({
        size: 'md',
        title,
        confirmText: 'General.Save',
        cancelText: 'General.Cancel',
        contentComponent: TripTypeSetupDialogComponent,
        hideFooter: true,
        data: data ? { ...data, id: this.selectedNode?.id } : undefined,
      })
      .closed.subscribe(({ responseData, source }) => {
        if (!responseData || source !== 'confirm') return;

        if (responseData.id && this.selectedNode) {
          // grid transactions without application provided id's can happen through object's reference
          this.selectedNode.data = responseData;
          this.gridApi?.applyTransaction({
            update: [this.selectedNode.data],
          });
        } else {
          this.gridApi?.applyTransaction({ add: [responseData] });
        }

        if (event) {
          this.emitGridDataChanged(event);
        }
      });
  }

  private applyNoRowsMessage(): void {
    const message = this.noRowsMessageKey
      ? this.translate.instant(this.noRowsMessageKey)
      : this.defaultNoRowsMessage;
    this.gridOptions.noRowsOverlayComponentParams = { message };
  }

  private performAccessControl(): void {
    if (this.actions().length) {
      this.actions.update(actions =>
        actions.map(action => {
          if (action.icon === 'add_box') {
            return { ...action, disabled: !this.hasEditPermission };
          }
          if (action.icon === 'edit') {
            return {
              ...action,
              disabled: !this.selectedNode || !this.hasEditPermission,
            };
          }
          if (action.icon === 'delete') {
            return {
              ...action,
              disabled: !this.selectedNode || !this.hasDeletePermission,
            };
          }
          return action;
        })
      );
    }
  }

  private emitGridDataChanged(event?: DvToolbarActionEvent): void {
    const api = event?.api ?? this.gridApi;
    if (!api) return;
    const rowData: TimeInterval[] = [];
    api.forEachNode(node => rowData.push(node.data));
    this.hasGridDataChanged.emit(rowData);
  }
}
